import { createTutorHttpClients } from '../../../api/tutorHttpBases';

const API = '/admin/prompt-library';
const clients = createTutorHttpClients();

export type PromptLibraryEntityType = 'template' | 'block' | 'dialect';

export interface PromptTemplateAdmin {
  id: string;
  key: string;
  name: string;
  module: string;
  submodule: string;
  mode: string;
  grade: number | null;
  language: string;
  status: string;
  version: number;
  template?: string;
  type: PromptLibraryEntityType;
  /** Present when POST/PUT template used auto_create_binding (`created` | `updated` | `skipped` when from API). */
  binding_status?: string | null;
  binding_reason?: string | null;
  binding_id?: string | null;
}

export interface PromptBlockAdmin {
  id: string;
  key: string;
  block_type: string;
  name: string;
  module: string;
  submodule: string;
  status: string;
  version: number;
  content?: string;
  type: PromptLibraryEntityType;
}

export interface DialectProfileAdmin {
  id: string;
  key: string;
  language: string;
  name: string;
  status: string;
  version: number;
  description?: string;
  instruction_block?: string;
  type: PromptLibraryEntityType;
}

type RecordLike = Record<string, unknown>;

async function getWithFallback<T>(path: string): Promise<T> {
  let lastError: unknown = null;
  for (const c of clients) {
    try {
      const r = await c.get<T>(path);
      return r.data;
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError;
}

async function postWithFallback<T>(path: string, body: unknown): Promise<T> {
  let lastError: unknown = null;
  for (const c of clients) {
    try {
      const r = await c.post<T>(path, body);
      return r.data;
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError;
}

async function putWithFallback<T>(path: string, body: unknown): Promise<T> {
  let lastError: unknown = null;
  for (const c of clients) {
    try {
      const r = await c.put<T>(path, body);
      return r.data;
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError;
}

async function deleteWithFallback(path: string): Promise<{ ok: boolean; deleted: number }> {
  let lastError: unknown = null;
  for (const c of clients) {
    try {
      const r = await c.delete<{ ok: boolean; deleted: number }>(path);
      return r.data;
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError;
}

function inferType(item: RecordLike): PromptLibraryEntityType {
  if (item.type === 'template' || item.type === 'block' || item.type === 'dialect') {
    return item.type;
  }
  if (typeof item.block_type === 'string' && item.block_type.trim()) return 'block';
  if (typeof item.template === 'string') return 'template';
  if (typeof item.content === 'string') return 'block';
  if (typeof item.instruction_block === 'string') return 'dialect';
  return 'template';
}

function normalizeTemplate(item: RecordLike): PromptTemplateAdmin {
  return {
    id: String(item.id ?? ''),
    key: String(item.key ?? ''),
    name: String(item.name ?? ''),
    module: String(item.module ?? ''),
    submodule: String(item.submodule ?? ''),
    mode: String(item.mode ?? ''),
    grade: item.grade === null || typeof item.grade === 'number' ? (item.grade as number | null) : null,
    language: String(item.language ?? ''),
    status: String(item.status ?? ''),
    version: Number(item.version ?? 1),
    template: typeof item.template === 'string' ? item.template : undefined,
    type: inferType(item),
    binding_status: typeof item.binding_status === 'string' ? item.binding_status : undefined,
    binding_reason: typeof item.binding_reason === 'string' ? item.binding_reason : undefined,
    binding_id: typeof item.binding_id === 'string' ? item.binding_id : undefined,
  };
}

function normalizeBlock(item: RecordLike): PromptBlockAdmin {
  return {
    id: String(item.id ?? ''),
    key: String(item.key ?? ''),
    block_type: String(item.block_type ?? ''),
    name: String(item.name ?? ''),
    module: String(item.module ?? ''),
    submodule: String(item.submodule ?? ''),
    status: String(item.status ?? ''),
    version: Number(item.version ?? 1),
    content: typeof item.content === 'string' ? item.content : undefined,
    type: inferType(item),
  };
}

function normalizeDialect(item: RecordLike): DialectProfileAdmin {
  return {
    id: String(item.id ?? ''),
    key: String(item.key ?? ''),
    language: String(item.language ?? ''),
    name: String(item.name ?? ''),
    status: String(item.status ?? ''),
    version: Number(item.version ?? 1),
    description: typeof item.description === 'string' ? item.description : undefined,
    instruction_block: typeof item.instruction_block === 'string' ? item.instruction_block : undefined,
    type: inferType(item),
  };
}

export async function getPromptLibraryTemplates(): Promise<PromptTemplateAdmin[]> {
  const data = await getWithFallback<RecordLike[]>(`${API}/templates`);
  return (data ?? []).map(normalizeTemplate);
}

export async function getPromptLibraryBlocks(): Promise<PromptBlockAdmin[]> {
  const data = await getWithFallback<RecordLike[]>(`${API}/blocks`);
  return (data ?? []).map(normalizeBlock);
}

export async function getPromptLibraryDialects(): Promise<DialectProfileAdmin[]> {
  const data = await getWithFallback<RecordLike[]>(`${API}/dialects`);
  return (data ?? []).map(normalizeDialect);
}

export async function updatePromptLibraryTemplate(
  id: string,
  payload: {
    name?: string;
    template?: string;
    status?: 'active' | 'inactive';
    description?: string;
    updated_by?: string;
    tenant_id?: string | null;
    allow_global_binding?: boolean;
    auto_create_binding?: boolean;
    binding_priority?: number;
    base_rules_key?: string;
    lesson_type_rules_key?: string;
    mode_rules_key?: string;
    source_rules_key?: string;
    final_output_check_key?: string;
    communication_rules_key?: string;
    // Picture Talk support
    picture_talk_rules_key?: string;
    dialect_rules_key?: string;
    event_rules_key?: string;
    lesson_type_rule_map?: Record<string, string>;
    binding_default_dialect_key?: string;
    default_dialect_key?: string;
    overwrite_existing_block_mappings?: boolean;
  }
): Promise<PromptTemplateAdmin> {
  const data = await putWithFallback<RecordLike>(`${API}/templates/${id}`, payload);
  return normalizeTemplate(data ?? {});
}

/** Removes all Mongo rows for this logical template (all versions). */
export async function deletePromptLibraryTemplate(id: string): Promise<void> {
  await deleteWithFallback(`${API}/templates/${encodeURIComponent(id)}`);
}

/** Removes all Mongo rows for this logical block (all versions). */
export async function deletePromptLibraryBlock(id: string): Promise<void> {
  await deleteWithFallback(`${API}/blocks/${encodeURIComponent(id)}`);
}

/** Removes all Mongo rows for this dialect key (all versions). */
export async function deleteDialectProfileGroupById(id: string): Promise<void> {
  await deleteWithFallback(`${API}/dialects/by-id/${encodeURIComponent(id)}`);
}

export async function createPromptLibraryTemplate(payload: {
  key: string;
  name: string;
  product?: string;
  module?: string;
  submodule: string;
  mode: 'FREE' | 'TOPIC' | 'LESSON' | 'LIVE';
  grade?: number | null;
  language?: string;
  status?: 'active' | 'inactive';
  version?: number;
  template_format?: string;
  template: string;
  variables?: string[];
  supported_lesson_types?: string[];
  supported_dialects?: string[];
  description?: string;
  tags?: string[];
  created_by?: string;
  tenant_id?: string | null;
  allow_global_binding?: boolean;
  auto_create_binding?: boolean;
  binding_priority?: number;
  base_rules_key?: string;
  lesson_type_rules_key?: string;
  mode_rules_key?: string;
  source_rules_key?: string;
  final_output_check_key?: string;
  communication_rules_key?: string;
  // Picture Talk support
  picture_talk_rules_key?: string;
  dialect_rules_key?: string;
  event_rules_key?: string;
  lesson_type_rule_map?: Record<string, string>;
  binding_default_dialect_key?: string;
  default_dialect_key?: string;
  overwrite_existing_block_mappings?: boolean;
}): Promise<PromptTemplateAdmin> {
  const data = await postWithFallback<RecordLike>(`${API}/templates`, {
    ...payload,
    product: payload.product ?? 'tamil_school',
    module: payload.module ?? 'ai_tutor',
    language: payload.language ?? 'ta',
    created_by: payload.created_by ?? 'devteam_ui',
  });
  return normalizeTemplate(data ?? {});
}

export async function updatePromptLibraryBlock(
  id: string,
  payload: {
    name?: string;
    content?: string;
    status?: 'active' | 'inactive';
    priority?: number;
    tags?: string[];
    updated_by?: string;
  }
): Promise<PromptBlockAdmin> {
  const data = await putWithFallback<RecordLike>(`${API}/blocks/${id}`, payload);
  return normalizeBlock(data ?? {});
}

export async function createPromptLibraryBlock(payload: {
  key: string;
  block_type:
    | 'base_rules'
    | 'lesson_type_rules'
    | 'mode_rules'
    | 'source_rules'
    | 'final_output_check'
    | 'communication_rules'
    | 'picture_talk_rules'
    | 'dialect_rules'
    | 'event_rules'
    | 'safety_rules';
  name: string;
  module?: string;
  submodule: string;
  content: string;
  applies_to?: string[];
  content_format?: string;
  status?: 'active' | 'inactive';
  version?: number;
  priority?: number;
  tags?: string[];
  created_by?: string;
}): Promise<PromptBlockAdmin> {
  const data = await postWithFallback<RecordLike>(`${API}/blocks`, {
    ...payload,
    module: payload.module ?? 'ai_tutor',
    applies_to: payload.applies_to ?? [],
    content_format: payload.content_format ?? 'plain',
    created_by: payload.created_by ?? 'devteam_ui',
  });
  return normalizeBlock(data ?? {});
}

export async function updateDialectProfile(
  dialectKey: string,
  payload: {
    name?: string;
    status?: 'active' | 'inactive';
    description?: string;
    instruction_block?: string;
    updated_by?: string;
  }
): Promise<DialectProfileAdmin> {
  const data = await putWithFallback<RecordLike>(`${API}/dialects/${encodeURIComponent(dialectKey)}`, payload);
  return normalizeDialect(data ?? {});
}

/** Prefer this when multiple versions share the same `key`. */
export async function updateDialectProfileById(
  profileId: string,
  payload: {
    name?: string;
    status?: 'active' | 'inactive';
    description?: string;
    instruction_block?: string;
    updated_by?: string;
  }
): Promise<DialectProfileAdmin> {
  const data = await putWithFallback<RecordLike>(`${API}/dialects/by-id/${profileId}`, payload);
  return normalizeDialect(data ?? {});
}

export async function activatePromptTemplateVersion(id: string): Promise<PromptTemplateAdmin> {
  const data = await postWithFallback<RecordLike>(`${API}/templates/${id}/activate-version`, {});
  return normalizeTemplate(data ?? {});
}

export async function activatePromptBlockVersion(id: string): Promise<PromptBlockAdmin> {
  const data = await postWithFallback<RecordLike>(`${API}/blocks/${id}/activate-version`, {});
  return normalizeBlock(data ?? {});
}

export async function activateDialectProfileVersion(id: string): Promise<DialectProfileAdmin> {
  const data = await postWithFallback<RecordLike>(`${API}/dialects/by-id/${id}/activate-version`, {});
  return normalizeDialect(data ?? {});
}

export async function createDialectProfile(payload: {
  key: string;
  name: string;
  language?: string;
  region?: string | null;
  category?: string | null;
  status?: 'active' | 'inactive';
  version?: number;
  description?: string;
  instruction_block: string;
  style_rules?: string[];
  preferred_terms?: string[];
  avoid_terms?: string[];
  tags?: string[];
  created_by?: string;
}): Promise<DialectProfileAdmin> {
  const data = await postWithFallback<RecordLike>(`${API}/dialects`, {
    ...payload,
    language: payload.language ?? 'ta',
    created_by: payload.created_by ?? 'devteam_ui',
  });
  return normalizeDialect(data ?? {});
}

export interface PromptBindingAdmin {
  id: string;
  tenant_id?: string | null;
  module: string;
  submodule: string;
  mode: string;
  grade?: number | null;
  language: string;
  prompt_template_key: string;
  prompt_template_version?: number;
  default_dialect_key: string;
  block_mappings: Record<string, string>;
  lesson_type_rule_map?: Record<string, string>;
  status: string;
  priority: number;
}

function normalizeBinding(item: RecordLike): PromptBindingAdmin {
  return {
    id: String(item.id ?? item._id ?? ''),
    tenant_id: typeof item.tenant_id === 'string' ? item.tenant_id : null,
    module: String(item.module ?? ''),
    submodule: String(item.submodule ?? ''),
    mode: String(item.mode ?? ''),
    grade: item.grade != null ? Number(item.grade) : null,
    language: String(item.language ?? 'ta'),
    prompt_template_key: String(item.prompt_template_key ?? ''),
    prompt_template_version: item.prompt_template_version != null ? Number(item.prompt_template_version) : undefined,
    default_dialect_key: String(item.default_dialect_key ?? 'standard_tamil'),
    block_mappings:
      typeof item.block_mappings === 'object' && item.block_mappings !== null
        ? (item.block_mappings as Record<string, string>)
        : {},
    lesson_type_rule_map:
      typeof item.lesson_type_rule_map === 'object' && item.lesson_type_rule_map !== null
        ? (item.lesson_type_rule_map as Record<string, string>)
        : undefined,
    status: String(item.status ?? 'active'),
    priority: Number(item.priority ?? 100),
  };
}

export async function getPromptLibraryBindings(): Promise<PromptBindingAdmin[]> {
  const data = await getWithFallback<RecordLike[]>(`${API}/bindings`);
  return (data ?? []).map(normalizeBinding);
}

function coerceOptionalStringField(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length > 0 ? s : null;
}

function coerceOptionalNumberField(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export type ResolvePictureTalkPromptResponse = {
  template_key: string;
  binding_id: string;
  module: string;
  submodule: string;
  dialect_key: string;
  final_prompt: string;
  blocks_used: string[];
  tenant_id?: string | null;
  selected_binding_scope?: string | null;
  selected_template_version?: number | null;
  requested_dialect_key?: string | null;
  resolved_dialect_key?: string | null;
};

/** Runtime resolver for Picture Talk dynamic prompts (new endpoint). Uses x-tenant-id from headers via client. */
export async function resolvePictureTalkPrompt(params: {
  grade?: number | null;
  dialect_key?: string;
  mode?: string;
  event_type: string;
  scene_context?: string;
  selected_item?: string;
  child_message?: string;
  lesson_no?: number;
}): Promise<ResolvePictureTalkPromptResponse> {
  const queryParams = new URLSearchParams({
    event_type: params.event_type,
    mode: params.mode || 'LIVE',
  });
  if (params.grade != null) queryParams.append('grade', params.grade.toString());
  if (params.dialect_key) queryParams.append('dialect_key', params.dialect_key);
  if (params.scene_context) queryParams.append('scene_context', params.scene_context);
  if (params.selected_item) queryParams.append('selected_item', params.selected_item);
  if (params.child_message) queryParams.append('child_message', params.child_message);
  if (params.lesson_no !== undefined) queryParams.append('lesson_no', params.lesson_no.toString());

  const data = await getWithFallback<RecordLike>(`${API}/resolve-picture-talk-prompt?${queryParams.toString()}`);
  return {
    template_key: String(data.template_key || 'picture_talk_default'),
    binding_id: String(data.binding_id || 'unknown'),
    module: String(data.module || 'picture_talk'),
    submodule: String(data.submodule || 'conversation'),
    dialect_key: String(data.dialect_key || params.dialect_key || 'eelam_tamil'),
    final_prompt: String(data.final_prompt || data.system_instruction || ''),
    blocks_used: Array.isArray(data.blocks_used) ? data.blocks_used : [],
    tenant_id: data.tenant_id == null ? null : String(data.tenant_id),
    selected_binding_scope: coerceOptionalStringField(data.selected_binding_scope),
    selected_template_version: coerceOptionalNumberField(data.selected_template_version),
    requested_dialect_key: coerceOptionalStringField(data.requested_dialect_key),
    resolved_dialect_key: coerceOptionalStringField(data.resolved_dialect_key),
  };
}
